import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BdAdminPermissionService,
  BdPermissionsCompanyAdminService,
  BdShareablesService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  BdCompany,
  BdCompanyGroup,
} from '@bindoc-projects/resource-domain-lib';
import { IftaLabel } from 'primeng/iftalabel';
import { Select, SelectChangeEvent } from 'primeng/select';
import { EMPTY, Observable, take } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';

@Component({
  selector: 'bd-group-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-iftalabel>
      <p-select
        [options]="groupsOfCurrentCompany()"
        [filter]="true"
        filterBy="name"
        optionLabel="name"
        fluid
        variant="filled"
        appendTo="body"
        (onChange)="onChange($event)"
      ></p-select>
      <label>Gruppe</label>
    </p-iftalabel>
  `,
  imports: [FormsModule, Select, IftaLabel],
})
export class BdGroupSelectComponent implements OnInit {
  public readonly company = input<BdCompany | undefined>(undefined);
  public readonly groupSelect = output<BdCompanyGroup>();

  public readonly groupsOfCurrentCompany = signal<BdCompanyGroup[]>([]);

  private static readonly GROUP_PAGE_SIZE = 100;

  private readonly _adminCompanyService = inject(
    BdPermissionsCompanyAdminService,
  );
  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _adminPermissions = inject(BdAdminPermissionService);

  private readonly _isAdmin = signal(false);

  constructor() {
    effect(() => {
      const company = this.company();
      if (!company) {
        this.groupsOfCurrentCompany.set([]);
        return;
      }
      this._loadGroupsFor(company);
    });
  }

  ngOnInit(): void {
    this._adminPermissions
      .containsPermission(['ADMIN:GROUPS'])
      .pipe(take(1))
      .subscribe((hasAdmin) => this._isAdmin.set(hasAdmin));
  }

  public onChange(event: SelectChangeEvent): void {
    if (event.value) {
      this.groupSelect.emit(event.value as BdCompanyGroup);
    }
  }

  private _loadGroupsFor(company: BdCompany): void {
    if (this._isAdmin()) {
      this._adminCompanyService
        .getGroups(company.id)
        .subscribe((page) => this.groupsOfCurrentCompany.set(page.content));
    } else {
      // Load the company's groups directly from the paged endpoint instead of
      // deriving them from the (fetch-all) company scopes.
      this._allGroupsOf(company.id).subscribe((groups) =>
        this.groupsOfCurrentCompany.set(groups),
      );
    }
  }

  /** Walks every page of a single company's groups (bounded, on demand). */
  private _allGroupsOf(companyId: string): Observable<BdCompanyGroup[]> {
    const size = BdGroupSelectComponent.GROUP_PAGE_SIZE;
    return this._resourcesService.groupsOfCompany(companyId, 0, size).pipe(
      expand((page) =>
        page.last ||
        page.content.length === 0 ||
        page.number + 1 >= page.totalPages
          ? EMPTY
          : this._resourcesService.groupsOfCompany(
              companyId,
              page.number + 1,
              size,
            ),
      ),
      reduce(
        (all: BdCompanyGroup[], page) => all.concat(page.content),
        [] as BdCompanyGroup[],
      ),
    );
  }
}
